import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { obterIndicadores, enviarContatoParceria } from "../services/api";

// PÁGINA DE PARCERIA — convite a uma causa, não cardápio de serviços.
//
// O enquadramento é decisão do Nelson (28/08/2026): "apresentar o projeto e convidar a parceria, abraçar a
// ideia de um processo seletivo mais justo". A diferença é prática, não estética — uma página que vende
// pacote convida a negociar preço; uma que convida a um compromisso convida a assumir posição.
//
// ⚠️ A RESSALVA DE IMPARCIALIDADE FICA EM DESTAQUE, NUNCA EM LETRA MIÚDA. O eaiRH já recusou anúncios para
// não comprometer a neutralidade; uma página de parceria que insinuasse vantagem na nota custaria a mesma
// coisa — e seria pior, porque estaria escrita.
export default function ParaEmpresasPage() {
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({
    empresa: "", nome: "", cargo: "", email: "", telefone: "", interesse: "vaga", mensagem: "",
  });
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState("");

  // Os números da prova social vêm do PORTAL, não de estimativa de mercado: é o dado que a gente pode
  // defender se alguém perguntar de onde saiu.
  useEffect(() => { obterIndicadores().then((d) => setStats(d?.geral || null)).catch(() => {}); }, []);

  const campo = (k) => ({
    value: form[k],
    onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
  });

  async function enviar(e) {
    e.preventDefault();
    setErro("");
    setEnviando(true);
    try {
      await enviarContatoParceria(form);
      setEnviado(true);
    } catch (ex) {
      setErro(ex.message || "Não consegui enviar agora. Tente novamente em instantes.");
    } finally {
      setEnviando(false);
    }
  }

  const semResposta = stats ? Math.round(stats.percSemResposta || 0) : null;

  // BOTAO FIXO QUE ROLA ATE O FORMULARIO — deliberadamente NAO e modal.
  //
  // A pagina e um CONVITE: os tres blocos antes do formulario (o que muda, o que a parceria nao e, a
  // ressalva de imparcialidade) sao o que faz a empresa entender no que esta entrando. Modal deixa preencher
  // sem ler nada disso, e o contato chega ao comercial sem contexto nenhum.
  //
  // VISIVEL DESDE A ENTRADA. A primeira versao so mostrava apos rolar 320px, para "nao empurrar o CTA antes
  // da leitura" — o Nelson apontou como falha em 28/08, e com razao: o botao vive no rodape e nao atrapalha
  // leitura nenhuma. Escondido, ele so fazia quem entrou nao descobrir que existe uma porta.
  //
  // A unica regra que sobra e sumir quando o FORMULARIO esta na tela: botao fixo cobrindo o proprio destino
  // e estorvo, e o formulario ja tem o proprio botao de enviar.
  const alvoRef = useRef(null);
  const [mostrarCta, setMostrarCta] = useState(true);
  useEffect(() => {
    const alvo = alvoRef.current;
    if (!alvo || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(([e]) => setMostrarCta(!e.isIntersecting), {
      rootMargin: "-120px 0px 0px 0px",
    });
    obs.observe(alvo);
    return () => obs.disconnect();
  }, []);

  const irParaFormulario = () =>
    alvoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <section className="bg-gradient-to-br from-brand to-primary text-white">
        <div className="mx-auto max-w-4xl px-5 py-14">
          <p className="text-sm font-semibold uppercase tracking-wide text-white/70">Para empresas</p>
          <h1 className="mt-2 text-3xl md:text-4xl font-extrabold leading-tight">
            Processo seletivo justo não é o que a empresa promete.<br />É o que o candidato viveu.
          </h1>
          <p className="mt-4 text-white/90 text-lg leading-relaxed">
            O eaiRH é o portal onde quem se candidatou registra como foi o processo — e onde a empresa
            responde publicamente, na mesma página. Estamos convidando empresas que topam assumir um
            compromisso simples: <b>ninguém sai do processo sem saber que saiu</b>.
          </p>

          {/* 💰 O PREÇO VEM ANTES DA TESE (16/09/2026). A Marli Reis, da TEL Centro de Contatos, leu esta página
              por 7 minutos e 37 segundos e mesmo assim a primeira pergunta do contato dela foi "há algum custo
              para utilizar o serviço?". Quem lê tudo e sai sem essa resposta é o melhor caso; o resto vai embora
              sem escrever. "De graça" aparecia só no meio do terceiro bloco — agora abre a página. */}
          <div className="mt-6 rounded-2xl bg-white/12 ring-1 ring-white/25 px-5 py-4">
            <p className="text-lg font-bold">Publicar vaga aqui é gratuito.</p>
            <p className="mt-1 text-white/85 leading-relaxed">
              Sem plano, sem limite de vagas e sem cobrança por candidatura. Responder aos relatos também é
              gratuito para qualquer empresa, parceira ou não.
            </p>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-4xl px-5 py-12 space-y-14">
        <section>
          <h2 className="text-2xl font-bold text-text">O problema, em números do próprio portal</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            {[
              { n: semResposta === null ? "—" : `${semResposta}%`, l: "dos processos relatados terminam sem resposta nenhuma", warn: true },
              { n: stats?.totalDenuncias ?? "—", l: "relatos de candidatos registrados" },
              { n: stats?.totalEmpresas ?? "—", l: "empresas com reputação de processo no portal" },
            ].map((x, i) => (
              <div key={i} className="rounded-2xl border border-gray-200 bg-white p-5 text-center shadow-sm">
                <div className={`text-3xl font-extrabold ${x.warn ? "text-orange-600" : "text-primary"}`}>{x.n}</div>
                <div className="mt-1 text-sm text-gray-600">{x.l}</div>
              </div>
            ))}
          </div>
          <p className="mt-5 text-gray-600 leading-relaxed">
            Silêncio raramente é má vontade de quem recruta. É gargalo de aprovação, fila que ninguém vê e
            ausência de consequência. Enquanto não responder não custa nada, continua sendo a opção mais
            barata — e quem paga a conta é o candidato e a marca da empresa.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-text">O que muda quando uma empresa entra</h2>
          <div className="mt-5 space-y-4">
            {[
              { t: "Publicar vagas, de graça", d: "A vaga ganha página própria, indexada no Google, com a reputação da empresa ao lado. O candidato acompanha o andamento sozinho — o que tira da sua caixa boa parte do \"e aí, saiu alguma coisa?\"." },
              { t: "Responder aos relatos", d: "Direito de resposta na mesma página do relato, com o mesmo destaque. Isso já é gratuito para todas as empresas, parceiras ou não — e sempre será." },
              { t: "Avisar todo mundo, automaticamente", d: "Integrando seu ATS, cada mudança de etapa dispara o aviso ao candidato com o texto que a sua empresa escreve. Você liga uma vez e para de depender de alguém lembrar. Já funciona hoje, em produção." },
            ].map((x, i) => (
              <div key={i} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="font-bold text-text">{x.t}</h3>
                <p className="mt-1.5 text-gray-600 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border-l-4 border-accent bg-orange-50 p-6">
          <h2 className="text-lg font-bold text-text">O que a parceria não é</h2>
          <p className="mt-2 text-gray-700 leading-relaxed">
            <b>Ser parceira não altera a reputação da empresa.</b> Não muda nota, não muda posição em ranking,
            não esconde relato e não dá destaque. A avaliação continua vindo de quem passou pelo processo, e o
            direito de resposta continua valendo para todas — inclusive para quem nunca falou com a gente.
          </p>
          <p className="mt-2 text-gray-700 leading-relaxed">
            O eaiRH não vende reputação, e nunca vai vender. É isso que faz o registro valer alguma coisa.
          </p>
        </section>

        {/* PROVA SOCIAL (18/09/2026). Até aqui a página só tinha a NOSSA palavra — e quem decide entrar
            quer ouvir de quem já está. Estes três comentaram PUBLICAMENTE na página do eaiRH no LinkedIn,
            com nome, cargo e empresa; nada aqui é anônimo nem parafraseado. Duas das três são gerentes da
            MESMA empresa (Gateware), o que é mais forte que três elogios soltos: é uma empresa inteira
            endossando. Fica logo antes do formulário de propósito — é onde a pessoa hesita. */}
        <section>
          <h2 className="text-2xl font-bold text-text">Quem já está por perto</h2>
          <p className="mt-2 text-gray-700">Comentários públicos na nossa página do LinkedIn.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            {[
              {
                texto: "A eaiRH é projeto necessário! Nós é quem agradecemos pela oportunidade de fazer parte, enquanto clientes e também apoiadores.",
                nome: "Frederico Inácio de Oliveira",
                cargo: "Transformando caos em case · No-code + IA",
              },
              {
                texto: "Parabéns pela iniciativa, sem dúvidas irá apoiar muitos profissionais na tomada de decisão! Conte com a Gateware para melhorar e alavancar ainda mais a plataforma!",
                nome: "Thatyane da Costa",
                cargo: "Gerente de Experiência do Cliente e Colaborador · Gateware",
              },
              {
                texto: "Parabéns pelo projeto e pela vontade de sempre fazer mais e melhor. Nossa conversa foi enriquecedora e você pode contar sempre conosco!",
                nome: "Taline Klinguelfus Arrabal",
                cargo: "Gerente de Marketing e Pessoas & Cultura · Gateware",
              },
            ].map((d) => (
              <figure key={d.nome} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <blockquote className="text-gray-800 leading-relaxed">“{d.texto}”</blockquote>
                <figcaption className="mt-3 border-t border-gray-100 pt-3">
                  <div className="font-bold text-text text-sm">{d.nome}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{d.cargo}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="contato" ref={alvoRef} className="scroll-mt-24">
          <h2 className="text-2xl font-bold text-text">Vamos conversar</h2>
          {/* Repetido de propósito: é aqui que a pessoa decide escrever, e é aqui que ela hesita por não ter
              certeza se vai receber uma proposta comercial depois. */}
          <p className="mt-1.5 text-gray-600">
            Respondemos em até 2 dias úteis. <b className="text-text">Não há cobrança em nenhuma etapa</b> —
            publicar vaga e responder relato são gratuitos.
          </p>

          {enviado ? (
            <div className="mt-5 rounded-2xl border border-green-200 bg-green-50 p-6">
              <h3 className="font-bold text-green-900">Recebemos seu contato.</h3>
              <p className="mt-1.5 text-green-800">
                Nosso time comercial retorna em até 2 dias úteis no e-mail que você informou. Obrigado por
                topar essa conversa.
              </p>
            </div>
          ) : (
            <form onSubmit={enviar} className="mt-5 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Campo rot="Empresa *" props={campo("empresa")} req />
                <Campo rot="Seu nome *" props={campo("nome")} req />
                <Campo rot="Cargo" props={campo("cargo")} />
                <Campo rot="E-mail *" props={campo("email")} tipo="email" req />
                <Campo rot="Telefone / WhatsApp" props={campo("telefone")} />
                <label className="block">
                  <span className="text-sm font-semibold text-gray-700">O que você procura</span>
                  <select
                    {...campo("interesse")}
                    className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="vaga">Publicar vagas</option>
                    <option value="resposta">Responder relatos da minha empresa</option>
                    <option value="integracao">Integrar meu ATS</option>
                    <option value="outro">Outro assunto</option>
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="text-sm font-semibold text-gray-700">Mensagem</span>
                <textarea
                  {...campo("mensagem")}
                  rows={4}
                  placeholder="Conte rapidamente sobre seus processos seletivos: quantas vagas por mês, qual ferramenta usam hoje, o que mais incomoda."
                  className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </label>

              {erro && <p className="text-sm font-semibold text-red-600">{erro}</p>}

              <button
                type="submit"
                disabled={enviando}
                className="w-full rounded-xl bg-primary px-6 py-3 font-bold text-white transition hover:brightness-110 disabled:opacity-60"
              >
                {enviando ? "Enviando..." : "Quero conversar sobre parceria"}
              </button>
              <p className="text-xs text-gray-500">
                Seus dados vão só para o nosso time comercial. Não compartilhamos com terceiros.
              </p>
            </form>
          )}
        </section>
      </main>

      {/* Fica acima do rodape e respeita area segura de iPhone. Escondido de leitores de tela quando oculto. */}
      {!enviado && (
        <div
          className={`fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] transition-all duration-300 ${
            mostrarCta ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-6 opacity-0"
          }`}
          aria-hidden={!mostrarCta}
        >
          <button
            type="button"
            onClick={irParaFormulario}
            tabIndex={mostrarCta ? 0 : -1}
            className="rounded-full bg-accent px-7 py-3.5 font-bold text-white shadow-lg shadow-black/20 transition hover:brightness-110"
          >
            Quero conversar sobre parceria
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
}

function Campo({ rot, props, tipo = "text", req = false }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-gray-700">{rot}</span>
      <input
        type={tipo}
        required={req}
        {...props}
        className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
    </label>
  );
}
